/**
 * Combined four-module performance trends line chart with toggleable legend (B6)
 * and day-aggregated tooltip (B4).
 *
 * @module PerformanceTrendsCombinedChart
 */

import { useCallback, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { TooltipProps } from 'recharts';
import { useTranslation } from 'react-i18next';

import {
  SIMULATION_MODULE_COLORS,
  SIMULATION_MODULE_KEYS,
  type ChartDataPoint,
  type SimulationModuleKey,
} from '@/lib/simulationResultsChartData';
import { cn } from '@/lib/utils';

export interface PerformanceTrendsCombinedChartProps {
  readonly chartData: ChartDataPoint[];
  readonly attemptedModules: ReadonlySet<SimulationModuleKey>;
  /** AC4: navigate to latest graded review for a module (legend click). */
  readonly onModuleReviewClick?: (module: SimulationModuleKey) => void;
  readonly className?: string;
}

type TrendTooltipProps = TooltipProps<number, string>;

function PerformanceTrendsTooltip({ active, payload, label }: TrendTooltipProps) {
  const { t } = useTranslation();

  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0]?.payload as ChartDataPoint | undefined;
  const attemptCount = point?.attemptCount ?? 1;

  return (
    <div className="font-ui rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-md">
      <p className="mb-1.5 font-semibold text-foreground">{label}</p>
      {attemptCount > 1 ? (
        <p className="mb-2 text-muted-foreground">
          {t('simulation.results.performanceTrends.attemptsOnDay', {
            count: attemptCount,
            defaultValue: '{{count}} attempts on this day',
          })}
        </p>
      ) : null}
      <ul className="space-y-1">
        {payload.map((entry) => {
          if (entry.value === null || entry.value === undefined) {
            return null;
          }
          return (
            <li key={entry.dataKey} className="flex items-center justify-between gap-4">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <span
                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: entry.color }}
                  aria-hidden
                />
                {entry.name}
              </span>
              <span className="font-semibold tabular-nums text-foreground">{entry.value}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function PerformanceTrendsCombinedChart({
  chartData,
  attemptedModules,
  onModuleReviewClick,
  className = 'h-[300px]',
}: PerformanceTrendsCombinedChartProps) {
  const { t } = useTranslation();
  const [hiddenModules, setHiddenModules] = useState<ReadonlySet<SimulationModuleKey>>(
    () => new Set(),
  );

  const visibleModuleKeys = useMemo(
    () => SIMULATION_MODULE_KEYS.filter((key) => attemptedModules.has(key)),
    [attemptedModules],
  );

  const showLegend = chartData.length > 1 && visibleModuleKeys.length > 0;

  const handleLegendClick = useCallback(
    (dataKey: string, event: { shiftKey?: boolean }) => {
      const moduleKey = dataKey as SimulationModuleKey;
      if (!SIMULATION_MODULE_KEYS.includes(moduleKey)) {
        return;
      }
      if (event.shiftKey) {
        setHiddenModules((prev) => {
          const next = new Set(prev);
          if (next.has(moduleKey)) {
            next.delete(moduleKey);
          } else {
            next.add(moduleKey);
          }
          return next;
        });
        return;
      }
      onModuleReviewClick?.(moduleKey);
    },
    [onModuleReviewClick],
  );

  const moduleLabels: Record<SimulationModuleKey, string> = {
    reading: t('moduleSelection.modules.reading.label', { defaultValue: 'Reading' }),
    writing: t('moduleSelection.modules.writing.label', { defaultValue: 'Writing' }),
    listening: t('moduleSelection.modules.listening.label', { defaultValue: 'Listening' }),
    speaking: t('moduleSelection.modules.speaking.label', { defaultValue: 'Speaking' }),
  };

  const legendHint =
    onModuleReviewClick && showLegend
      ? t('simulation.results.performanceTrends.legendHint', {
          defaultValue: 'Click a module to review your latest attempt. Shift+click to show or hide lines.',
        })
      : null;

  return (
    <div className="flex flex-col gap-2">
      {legendHint ? (
        <p className="font-ui text-xs text-muted-foreground">{legendHint}</p>
      ) : null}
      <div className={cn(className)}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis dataKey="date" className="text-xs" />
          <YAxis domain={[0, 100]} className="text-xs" />
          <Tooltip content={<PerformanceTrendsTooltip />} />
          {showLegend ? (
            <Legend
              onClick={(entry, _index, event) => {
                if (entry.dataKey) {
                  handleLegendClick(String(entry.dataKey), event);
                }
              }}
              wrapperStyle={{ cursor: 'pointer' }}
            />
          ) : null}
          {visibleModuleKeys.map((moduleKey) => (
            <Line
              key={moduleKey}
              type="monotone"
              dataKey={moduleKey}
              stroke={SIMULATION_MODULE_COLORS[moduleKey]}
              strokeWidth={2}
              dot={{ fill: SIMULATION_MODULE_COLORS[moduleKey], r: 4 }}
              activeDot={{ r: 6 }}
              name={moduleLabels[moduleKey]}
              connectNulls
              hide={hiddenModules.has(moduleKey)}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
}
